import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const milestones = [
  { year: '2020', title: 'CoreNext成立', desc: '由前芯片架构师与HPC科学家联合创立，确立2048核并行计算为研究方向。' },
  { year: '2021', title: '首个原型流片', desc: '完成128核原型芯片流片验证，片上网络互联延迟降低40%。' },
  { year: '2022', title: '获A轮融资', desc: '获得顶级半导体投资机构战略投资，估值突破10亿人民币。' },
  { year: '2023', title: '512核芯片发布', desc: '发布首款512核商用芯片，在AI推理市场实现突破，客户包括多家头部云厂商。' },
  { year: '2024', title: '2048核芯片流片成功', desc: '采用3nm制程工艺，2048核芯片一次流片成功，性能超预期30%。' },
  { year: '2025', title: '大规模商用', desc: '2048核方案在超算中心、AI大模型训练等场景大规模部署，服务全球超过50家企业客户。' },
  { year: '2026', title: '技术白皮书发布', desc: '发布2048核处理器全栈技术白皮书，开放架构设计方法论与最佳实践。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') {
          body.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9phqemb4qjhggaepuo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交异常，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于 CoreNext 算力研究院',
            description: 'CoreNext 算力研究院是专注于2048核处理器技术研究与分析的专业机构，致力于推动超大规模并行计算技术发展。',
            url: 'https://www.corenext-tech.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: 'CoreNext 算力研究院',
              url: 'https://www.corenext-tech.com/',
            },
          }),
        }}
      />
      <main>
        <Navbar />

        <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-3">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="pb-16 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full mb-4">
                  品牌介绍
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-5">
                  关于 CoreNext 算力研究院
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  <strong>CoreNext 算力研究院</strong>成立于2020年，是全球领先的超大规模并行计算处理器技术研究机构。
                  我们汇聚了来自全球顶尖半导体公司与高性能计算实验室的资深架构师和科学家，专注于推动2048核及以上规模处理器技术的边界。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的研究覆盖<strong>片上网络架构</strong>、近存计算、存算一体、安全隔离、3D堆叠集成等前沿领域。
                  2024年，我们成功完成2048核芯片流片，性能超出预期30%，标志着并行计算进入了全新时代。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  至今，我们的技术方案已服务全球超过50家企业客户，涵盖<strong>AI训练平台</strong>、国家级超算中心、
                  <strong>云计算基础设施</strong>和金融风险建模等关键领域。CoreNext致力于让2048核的极致并行算力普惠每一个需要它的场景。
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20laboratory%20with%20warm%20orange%20accent%20lighting%2C%20clean%20semiconductor%20research%20environment%2C%20professional%20quality%2C%20abstract%20geometric%20chip%20design%20elements%2C%20futuristic%20aesthetic&width=800&height=600&seq=about-brand-01&orientation=portrait"
                  alt="CoreNext 算力研究院 - 研发实验室"
                  title="CoreNext 算力研究院"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                行业声誉
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                发展里程碑
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                从实验室原型到大规模商用，一路走来，每一步都坚实有力
              </p>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`relative flex flex-col lg:flex-row gap-6 items-start ${
                      idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                      <div className="bg-background-50 rounded-lg p-5">
                        <span className="inline-block px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-2">
                          {m.year}
                        </span>
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1.5">
                          {m.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-100 top-6"></div>
                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                获取专业2048核方案咨询
              </h2>
              <p className="text-sm text-foreground-500">
                填写以下表单，我们的技术专家将在24小时内与您联系
              </p>
            </div>

            <form
              id="contact-form"
              data-readdy-form="true"
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    公司名称
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的公司名称"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    电话
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的联系电话"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="interest" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  关注领域
                </label>
                <select
                  id="interest"
                  name="interest"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                >
                  <option value="">请选择关注的领域</option>
                  <option value="ai-training">AI大模型训练</option>
                  <option value="scientific-computing">科学计算与仿真</option>
                  <option value="datacenter">数据中心部署</option>
                  <option value="chip-design">芯片架构设计</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div className="mb-5">
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请描述您的需求（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-anti-spam"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium rounded-md text-sm transition-colors whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '提交咨询'}
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-accent-600 text-center">
                  提交成功！我们的技术专家将在24小时内与您联系。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-red-500 text-center">
                  {formError || '提交失败，请稍后重试'}
                </p>
              )}
            </form>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}